import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOBS } from "@/data/jobs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_OPTIONS = [
  "0 – 1 years",
  "1 – 3 years",
  "3 – 5 years",
  "5 – 8 years",
  "8+ years",
];
const POSITION_OPTIONS = [
  ...JOBS.map((j) => j.title),
  "Other / General application",
];
const MAX_MB = 5;

export default function ApplicationForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    linkedin: "",
    portfolio: "",
    cover_message: "",
    website: "", // honeypot
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  // Pre-fill position from #apply?position=... hash on load
  useEffect(() => {
    const applyFromHash = () => {
      const raw = window.location.hash || "";
      if (!raw.startsWith("#apply")) return;
      const qIdx = raw.indexOf("?");
      if (qIdx > -1) {
        const params = new URLSearchParams(raw.slice(qIdx + 1));
        const pos = params.get("position");
        if (pos && POSITION_OPTIONS.includes(pos)) {
          setForm((f) => ({ ...f, position: pos }));
        }
      }
      // Scroll to form
      const el = document.getElementById("apply");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    applyFromHash();
    window.addEventListener("hashchange", applyFromHash);
    return () => window.removeEventListener("hashchange", applyFromHash);
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext || "")) {
      toast.error("Only PDF, DOC or DOCX files are allowed.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Resume must be ${MAX_MB} MB or smaller.`);
      return;
    }
    setResume(f);
  };

  const clearFile = () => {
    setResume(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "email", "phone", "position", "experience"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    if (!resume) {
      toast.error("Please attach your resume.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("resume", resume);
      await axios.post(`${API}/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success(
        "Application received. Our team will reach out within 3 business days."
      );
      setForm({
        full_name: "",
        email: "",
        phone: "",
        position: "",
        experience: "",
        linkedin: "",
        portfolio: "",
        cover_message: "",
        website: "",
      });
      clearFile();
    } catch (err) {
      let msg = "Something went wrong. Please try again.";
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length && detail[0]?.msg) {
        // FastAPI 422 validation: surface the first field error
        const first = detail[0];
        const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "field";
        msg = `${String(field).replaceAll("_", " ")}: ${first.msg}`;
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 w-full bg-transparent border-b border-white/20 focus:border-[color:var(--accent)] py-3 text-white placeholder:text-white/30 transition-colors";

  return (
    <section
      id="apply"
      data-testid="application-form-section"
      className="relative border-t border-hairline py-24 md:py-32 scroll-mt-24"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <span className="mono-label">[04] Apply</span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.7 }}
            className="mt-4 font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.98]"
          >
            Application
            <br />
            <span className="text-[color:var(--accent)]">form.</span>
          </motion.h2>
          <p className="mt-8 text-white/60 text-[15px] leading-relaxed max-w-[42ch]">
            Fill in the details below and attach your resume (PDF, DOC or DOCX, max 5 MB). We
            review every application and respond within three business days.
          </p>

          <div className="mt-10 space-y-6 border-t border-hairline pt-8">
            <div>
              <div className="mono-label mb-1">Studio</div>
              <div className="text-white text-[15px]">Pune, Maharashtra · India</div>
            </div>
            <div>
              <div className="mono-label mb-1">Hiring for</div>
              <div className="text-white text-[15px]">PHP · Node.js · React · UI · QA</div>
            </div>
            <div>
              <div className="mono-label mb-1">Response time</div>
              <div className="text-white text-[15px]">Within 3 business days</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            ref={formRef}
            onSubmit={submit}
            data-testid="application-form"
            noValidate
            className="border border-hairline bg-surface p-6 md:p-10"
          >
            {/* Honeypot — hidden from users */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />

            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="mono-label">Full name *</span>
                <input
                  data-testid="app-full-name"
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  required
                  className={inputCls}
                  placeholder="Your full name"
                />
              </label>
              <label className="block">
                <span className="mono-label">Email *</span>
                <input
                  data-testid="app-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  className={inputCls}
                  placeholder="you@email.com"
                />
              </label>
              <label className="block">
                <span className="mono-label">Phone *</span>
                <input
                  data-testid="app-phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange("phone")}
                  required
                  className={inputCls}
                  placeholder="+91 …"
                />
              </label>
              <label className="block">
                <span className="mono-label">LinkedIn</span>
                <input
                  data-testid="app-linkedin"
                  type="url"
                  value={form.linkedin}
                  onChange={onChange("linkedin")}
                  className={inputCls}
                  placeholder="https://linkedin.com/in/…"
                />
              </label>

              <div className="block">
                <span className="mono-label">Position applying for *</span>
                <div className="mt-2">
                  <Select
                    value={form.position}
                    onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}
                  >
                    <SelectTrigger
                      data-testid="app-position"
                      className="w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:border-[color:var(--accent)] focus:ring-0 focus:ring-offset-0 py-6 text-white h-auto justify-between"
                    >
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12151F] border border-hairline text-white">
                      {POSITION_OPTIONS.map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          data-testid={`position-option-${p}`}
                          className="focus:bg-[color:var(--accent)] focus:text-white cursor-pointer"
                        >
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="block">
                <span className="mono-label">Years of experience *</span>
                <div className="mt-2">
                  <Select
                    value={form.experience}
                    onValueChange={(v) => setForm((f) => ({ ...f, experience: v }))}
                  >
                    <SelectTrigger
                      data-testid="app-experience"
                      className="w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:border-[color:var(--accent)] focus:ring-0 focus:ring-offset-0 py-6 text-white h-auto justify-between"
                    >
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12151F] border border-hairline text-white">
                      {EXPERIENCE_OPTIONS.map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          data-testid={`experience-option-${p}`}
                          className="focus:bg-[color:var(--accent)] focus:text-white cursor-pointer"
                        >
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="block md:col-span-2">
                <span className="mono-label">Portfolio / GitHub</span>
                <input
                  data-testid="app-portfolio"
                  type="url"
                  value={form.portfolio}
                  onChange={onChange("portfolio")}
                  className={inputCls}
                  placeholder="https://github.com/… or your website"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mono-label">Cover message</span>
                <textarea
                  data-testid="app-cover"
                  value={form.cover_message}
                  onChange={onChange("cover_message")}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us briefly why you'd be a great fit."
                />
              </label>

              <div className="md:col-span-2">
                <span className="mono-label">Resume * (PDF, DOC, DOCX · max 5 MB)</span>
                <div className="mt-2">
                  {resume ? (
                    <div
                      data-testid="app-resume-selected"
                      className="flex items-center justify-between gap-4 border border-hairline bg-[#0e121b] px-4 py-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Upload size={16} className="text-[color:var(--accent)] shrink-0" />
                        <span className="text-white text-[14px] truncate">{resume.name}</span>
                        <span className="mono-label !text-white/40">
                          {(resume.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={clearFile}
                        data-testid="app-resume-clear"
                        aria-label="Remove resume"
                        className="text-white/70 hover:text-[color:var(--accent)]"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="app-resume-input"
                      className="flex items-center justify-between gap-4 border border-dashed border-white/20 hover:border-[color:var(--accent)] px-4 py-4 cursor-pointer transition-colors"
                    >
                      <span className="text-white/60 text-[14px]">
                        Click to upload or drag &amp; drop
                      </span>
                      <span className="inline-flex items-center gap-2 mono-label !text-white">
                        Choose file <Upload size={14} />
                      </span>
                    </label>
                  )}
                  <input
                    id="app-resume-input"
                    data-testid="app-resume-input"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onFile}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <p className="mono-label !text-white/50">
                By submitting, you agree to be contacted about this application.
              </p>
              <button
                type="submit"
                data-testid="app-submit"
                disabled={submitting}
                className={`group inline-flex items-center gap-3 px-6 py-4 text-[14px] font-medium tracking-tight transition-colors ${
                  done
                    ? "bg-white text-[color:var(--bg)]"
                    : "bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)]"
                } disabled:opacity-50`}
              >
                {done ? (
                  <>
                    Application sent <Check size={16} />
                  </>
                ) : (
                  <>
                    {submitting ? "Sending…" : "Submit application"} <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
